'use client';
import React from 'react';
import Link from 'next/link';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';

const programmes = [
  {
    tag: '[A]',
    title: 'Accelerator Programme',
    subtitle: 'Audit-ready · Fast · Embedded',
    description:
      'When you need to get your security posture up to speed quickly, ahead of an audit, a funding round, or a major customer onboarding. Senior engineers embedded directly into your organisation, doing the work at pace. Typically 6–12 weeks, fully scoped to your situation.',
    href: '/services/accelerator',
    highlight: true,
    useCases: ['Pre-audit readiness sprint', 'Investor due diligence', 'Post-incident hardening'],
    duration: '6–12 weeks',
  },
  {
    tag: '[B]',
    title: 'Bespoke Security Programmes',
    subtitle: 'Custom · Framework-mapped · Durable',
    description:
      'Not generic frameworks, security programmes designed specifically for your organisation, your technology, and your risk profile. Mapped to SOC2, ISO 27001, DORA, GDPR, and more. Built to last beyond the initial engagement.',
    href: '/services/security-programmes',
    highlight: false,
    useCases: ['SOC2 programme build', 'ISO 27001 implementation', 'DORA compliance'],
    duration: 'Ongoing',
  },
  {
    tag: '[C]',
    title: 'Security Focus Programme',
    subtitle: 'Auditor aligned · Framework mapped · No unnecessary work',
    description:
      'Too many findings, too many frameworks, not enough clarity. We build a prioritised, framework-aligned programme that cuts the unnecessary work and focuses your team on the controls that actually satisfy auditors.',
    href: '/services/security-focus',
    highlight: false,
    useCases: ['Framework prioritisation', 'Audit preparation', 'Control rationalisation'],
    duration: '8–16 weeks',
  },
  {
    tag: '[D]',
    title: 'IaC Security Programme',
    subtitle: 'Policy-as-code · Supply chain · Drift governance',
    description:
      'A structured, organisation-wide IaC security programme covering policy-as-code, supply chain security, secrets governance, and drift detection, embedded into your pipelines and your team.',
    href: '/services/iac-programme',
    highlight: false,
    useCases: ['Policy-as-code rollout', 'Supply chain hardening', 'Drift detection & governance'],
    duration: '10–20 weeks',
  },
];

const differentiators = [
  {
    tag: '01',
    title: 'Senior engineers, not consultants',
    body: 'Every programme is delivered by senior TechSonar engineers, not junior consultants or subcontractors. The people who scope the work are the people who do it.',
  },
  {
    tag: '02',
    title: 'Embedded, not advisory',
    body: 'We work inside your organisation, in your tools, your sprints, your Slack. Not a report that lands on your desk. Actual work, done by us, alongside your team.',
  },
  {
    tag: '03',
    title: 'Scoped to your situation',
    body: 'No off-the-shelf programmes. Every engagement is scoped to your technology, your risk profile, and your timeline. We do not sell hours, we deliver outcomes.',
  },
  {
    tag: '04',
    title: 'Auditor-aligned delivery',
    body: 'Everything we produce is designed to satisfy auditors. Evidence packages, control documentation, and remediation records that hold up under examination.',
  },
];

export default function ProgrammesPage() {
  useScrollAnimation();

  return (
    <div className="min-h-screen bg-[#050a0f] text-[#f5f5f8] noise-overlay-animated">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="orb orb-blue" style={{ width: '600px', height: '600px', top: '-150px', left: '-100px', opacity: 0.3 }} />
          <div className="orb orb-teal" style={{ width: '400px', height: '400px', bottom: '0', right: '5%', opacity: 0.2 }} />
        </div>
        <div className="relative z-10 max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="flex items-center gap-3 mb-6 fade-in-up">
            <Link href="/" className="font-mono-brand text-[0.65rem] text-[#555] uppercase tracking-widest hover:text-[#80edfa] transition-colors">Home</Link>
            <span className="text-[#333]">/</span>
            <span className="font-mono-brand text-[0.65rem] text-[#80edfa] uppercase tracking-widest">Programmes</span>
          </div>

          <div className="max-w-[900px]">
            <p className="font-mono-brand text-[0.7rem] text-[#80edfa] uppercase tracking-widest mb-4 fade-in-up" style={{ transitionDelay: '0.05s' }}>
              [Structured engagements]
            </p>
            <h1 className="font-heading text-[clamp(2.5rem,6vw,4.5rem)] text-[#f5f5f8] font-medium leading-[1.05] mb-6 fade-in-up" style={{ transitionDelay: '0.1s' }}>
              Security programmes built<br />
              <span className="text-[#80edfa]">around your organisation.</span>
            </h1>
            <p className="text-[#aaa] text-lg leading-relaxed max-w-[680px] fade-in-up" style={{ transitionDelay: '0.15s' }}>
              TechSonar programmes are not off-the-shelf engagements. Each one is scoped to your technology, your risk profile, and your timeline, delivered by senior engineers embedded directly into your team.
            </p>
          </div>
        </div>
      </section>

      {/* Programmes Grid */}
      <section className="border-t border-[#222] py-20 lg:py-28">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="mb-12 fade-in-up">
            <p className="font-mono-brand text-[0.65rem] text-[#555] uppercase tracking-widest mb-2">All programmes</p>
          </div>

          {/* Accelerator, featured full-width */}
          <div className="mb-px">
            <Link
              href={programmes?.[0]?.href}
              className="group block bg-[#0a0f14] border border-[#80edfa]/20 hover:border-[#80edfa]/50 p-8 lg:p-12 transition-all duration-300 fade-in-up"
            >
              <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-8">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-4">
                    <p className="font-mono-brand text-[0.65rem] text-[#80edfa] uppercase tracking-widest">{programmes?.[0]?.tag}</p>
                    <span className="font-mono-brand text-[0.6rem] text-[#80edfa] border border-[#80edfa]/40 px-2 py-0.5 uppercase tracking-widest">Most requested</span>
                  </div>
                  <h2 className="font-heading text-2xl lg:text-3xl text-[#f5f5f8] font-medium mb-3 group-hover:text-[#80edfa] transition-colors duration-300">{programmes?.[0]?.title}</h2>
                  <p className="font-mono-brand text-[0.6rem] text-[#444] uppercase tracking-widest mb-4">{programmes?.[0]?.subtitle}</p>
                  <p className="text-sm text-[#666] leading-relaxed max-w-[680px] group-hover:text-[#aaa] transition-colors duration-300 mb-6">{programmes?.[0]?.description}</p>
                  <div className="flex flex-wrap gap-2">
                    {programmes?.[0]?.useCases?.map((uc, i) => (
                      <span key={i} className="font-mono-brand text-[0.6rem] text-[#555] border border-[#222] px-3 py-1 uppercase tracking-widest group-hover:border-[#333] group-hover:text-[#666] transition-colors duration-300">{uc}</span>
                    ))}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-4 flex-shrink-0">
                  <div className="text-right">
                    <p className="font-mono-brand text-[0.6rem] text-[#444] uppercase tracking-widest mb-1">Typical duration</p>
                    <p className="font-heading text-xl text-[#80edfa]">{programmes?.[0]?.duration}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="font-mono-brand text-[0.65rem] text-[#444] uppercase tracking-widest group-hover:text-[#80edfa] transition-colors duration-300">Explore programme</span>
                    <svg className="w-4 h-4 text-[#333] group-hover:text-[#80edfa] transition-all duration-300 group-hover:translate-x-1" viewBox="0 0 16 16" fill="none">
                      <path d="M2 8H14M9 3L14 8L9 13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                </div>
              </div>
            </Link>
          </div>

          {/* Remaining 3 programmes, bento grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#222] mt-px">
            {programmes?.slice(1)?.map((prog, i) => (
              <Link
                key={i}
                href={prog?.href}
                className="group bg-[#050a0f] p-8 lg:p-10 flex flex-col justify-between min-h-[320px] hover:bg-[#0a0f14] transition-colors duration-300 fade-in-up"
                style={{ transitionDelay: `${i * 0.07}s` }}
              >
                <div>
                  <div className="flex items-start justify-between mb-6">
                    <p className="font-mono-brand text-[0.65rem] text-[#80edfa] uppercase tracking-widest">{prog?.tag}</p>
                    <svg className="w-4 h-4 text-[#333] group-hover:text-[#80edfa] transition-colors duration-300 group-hover:translate-x-1 group-hover:-translate-y-1 transform" viewBox="0 0 16 16" fill="none">
                      <path d="M3 13L13 3M13 3H6M13 3V10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <h2 className="font-heading text-xl lg:text-2xl text-[#f5f5f8] font-medium mb-2 group-hover:text-[#80edfa] transition-colors duration-300">{prog?.title}</h2>
                  <p className="font-mono-brand text-[0.6rem] text-[#444] uppercase tracking-widest mb-4">{prog?.subtitle}</p>
                  <p className="text-sm text-[#666] leading-relaxed group-hover:text-[#aaa] transition-colors duration-300">{prog?.description}</p>
                </div>
                <div className="mt-8">
                  <div className="flex flex-wrap gap-1.5 mb-6">
                    {prog?.useCases?.map((uc, j) => (
                      <span key={j} className="font-mono-brand text-[0.55rem] text-[#444] border border-[#1a1a1a] px-2 py-0.5 uppercase tracking-widest">{uc}</span>
                    ))}
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="font-mono-brand text-[0.6rem] text-[#333] uppercase tracking-widest">{prog?.duration}</span>
                    <span className="font-mono-brand text-[0.65rem] text-[#444] uppercase tracking-widest group-hover:text-[#80edfa] transition-colors duration-300">Learn more →</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Why TechSonar Programmes */}
      <section className="border-t border-[#222] py-20 lg:py-28">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_2fr] gap-16 lg:gap-24">
            <div className="fade-in-up">
              <p className="font-mono-brand text-[0.65rem] text-[#555] uppercase tracking-widest mb-4">Why our programmes</p>
              <h2 className="font-heading text-[clamp(1.8rem,3.5vw,2.8rem)] text-[#f5f5f8] font-medium leading-[1.1]">
                Not advisory.<br />
                <span className="text-[#80edfa]">Embedded.</span>
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-[#222]">
              {differentiators?.map((d, i) => (
                <div key={i} className="bg-[#050a0f] p-8 fade-in-up" style={{ transitionDelay: `${i * 0.07}s` }}>
                  <p className="font-mono-brand text-[0.6rem] text-[#80edfa] uppercase tracking-widest mb-4">[{d?.tag}]</p>
                  <h3 className="font-heading text-lg text-[#f5f5f8] font-medium mb-3">{d?.title}</h3>
                  <p className="text-sm text-[#666] leading-relaxed">{d?.body}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-[#222] py-20 lg:py-28">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-10 fade-in-up">
            <div className="max-w-[600px]">
              <p className="font-mono-brand text-[0.65rem] text-[#555] uppercase tracking-widest mb-4">[Get started]</p>
              <h2 className="font-heading text-[clamp(1.8rem,3.5vw,2.8rem)] text-[#f5f5f8] font-medium leading-[1.1] mb-4">
                Not sure which programme<br />
                <span className="text-[#80edfa]">fits your situation?</span>
              </h2>
              <p className="text-[#666] text-base leading-relaxed">
                Tell us where you are and what you are trying to achieve. We will scope the right engagement for your organisation, no generic proposals, no unnecessary work.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 flex-shrink-0">
              <Link href="/contact" className="btn-primary">
                Discuss your programme
              </Link>
              <Link
                href="/services"
                className="flex items-center gap-2 px-6 py-3 border border-[#333] text-[#aaa] hover:border-[#80edfa] hover:text-[#80edfa] transition-all duration-300 font-mono-brand text-[0.7rem] uppercase tracking-widest"
              >
                View all services
                <svg className="w-3 h-3" viewBox="0 0 12 12" fill="none">
                  <path d="M2 6H10M7 3L10 6L7 9" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
